import { Box, Stack } from "@mui/material";
import { useShipTerminalMutation } from "Api/Mutations/TerminalInventory/useShipTerminalMutation";
import {
  type TerminalInventoryListParams,
  useTerminalInventoryListQuery,
} from "Api/Queries/TerminalInventory/useTerminalInventoryListQuery";
import { EditTerminalDialog } from "Components/Admin/Terminals/EditTerminalDialog/EditTerminalDialog";
import { TerminalListTable } from "Components/Admin/Terminals/TerminalListTable";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { useCheckPermission } from "Hooks/usePermission";
import { useServerTable } from "Hooks/useServerTable";
import {
  Permission,
  TerminalFinancing,
  TerminalInventoryStatus,
  type TerminalInventoryListItemDto,
} from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { type ReactNode, useMemo, useState } from "react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { asEnumValue, asString } from "Utils/FilterUtils";

const RESOURCES = Resources.Admin.Terminals;

// Keep in sync with TerminalInventoryListFilterRequest.AllowedSortColumns on BE.
const SORT_COLUMN_MAP: Record<string, string> = {
  lastChangedAt: "DateUpdated",
  merchantName: "Merchant",
};

export const TerminalsPage = () => {
  const { t } = useResource();
  const canUpdate = useCheckPermission(
    AccessRightCodes.TerminalInventory,
    Permission.Update,
  );
  const canCreate = useCheckPermission(
    AccessRightCodes.TerminalInventory,
    Permission.Create,
  );
  const canDelete = useCheckPermission(
    AccessRightCodes.TerminalInventory,
    Permission.Delete,
  );

  // Editor open state: terminal = edit, terminal null = stock in a new piece.
  const [editor, setEditor] = useState<{
    terminal: TerminalInventoryListItemDto | null;
  } | null>(null);

  const { queryParams, tableProps } =
    useServerTable<TerminalInventoryListItemDto>({
      defaultSorting: [{ id: "lastChangedAt", desc: true }],
    });

  const apiParams = useMemo(
    (): TerminalInventoryListParams => ({
      offset: queryParams.offset,
      limit: queryParams.limit,
      sortBy: queryParams.sortBy
        ? (SORT_COLUMN_MAP[queryParams.sortBy] ?? queryParams.sortBy)
        : undefined,
      sortDirection: queryParams.sortDirection,
      status: asEnumValue(TerminalInventoryStatus, queryParams.filters.status),
      financing: asEnumValue(TerminalFinancing, queryParams.filters.financing),
      serialNumber: asString(queryParams.filters.serialNumber),
      tid: asString(queryParams.filters.tid),
      mid: asString(queryParams.filters.comgateMid),
      hardwareModel: asString(queryParams.filters.hardwareModel),
      hunterName: asString(queryParams.filters.hunterName),
      merchantName: asString(queryParams.filters.merchantName),
      facilityName: asString(queryParams.filters.facilityName),
    }),
    [queryParams],
  );

  const { data, isLoading, isError } = useTerminalInventoryListQuery(apiParams);

  const { mutate: shipTerminal, isPending: isShipPending } =
    useShipTerminalMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.Messages.ShipSuccess));
      },
      onError: () => {
        toast.error(t(RESOURCES.Messages.ShipError));
      },
    });

  if (!isLoading && (isError || !data)) {
    return (
      <Layout>
        <ErrorText>{t(RESOURCES.Messages.LoadError)}</ErrorText>
      </Layout>
    );
  }

  return (
    <Layout>
      <Stack gap={4}>
        <Box display="grid" alignItems="start">
          <TerminalListTable
            data={data?.items ?? []}
            isLoading={isLoading}
            isEditingEnabled={canUpdate}
            onEdit={(terminal) => setEditor({ terminal })}
            onAdd={canCreate ? () => setEditor({ terminal: null }) : undefined}
            onShip={
              canUpdate
                ? (terminal) => shipTerminal(terminal.publicID)
                : undefined
            }
            isShipPending={isShipPending}
            rowCount={data?.totalCount ?? 0}
            tableProps={tableProps}
          />
        </Box>
      </Stack>
      {editor && (
        <EditTerminalDialog
          isOpen
          terminal={editor.terminal}
          canDelete={canDelete}
          onClose={() => setEditor(null)}
        />
      )}
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout title={t(RESOURCES.PageTitle)}>
      <StyledPageWrapper maxWidth="unset !important">
        {children}
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default TerminalsPage;
